import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Store, AlertTriangle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BusinessForm } from "@/components/BusinessForm";
import { VendorInsights } from "@/components/VendorInsights";
import { VendorReviews } from "@/components/VendorReviews";
import { CATEGORY_MAP, imgSrc } from "@/lib/categories";
import { api, errorMessage, startCheckout } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function VendorDashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const active = user.subscription_status === "active";

  const load = () => api.get("/vendor/businesses").then((r) => setItems(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const save = async (form) => {
    setBusy(true);
    try {
      if (editing.id) await api.put(`/vendor/businesses/${editing.id}`, form);
      else await api.post("/vendor/businesses", form);
      toast.success(editing.id ? "Business updated" : "Business added");
      setEditing(null); load();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/vendor/businesses/${id}`); toast.success("Business removed"); load(); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  const subscribe = async () => { try { await startCheckout("vendor"); } catch (e) { toast.error(errorMessage(e)); } };

  return (
    <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#FFB800]">Vendor portal</p>
          <h1 className="text-4xl font-extrabold tracking-tight mt-1">Your businesses</h1>
        </div>
        <Button data-testid="vendor-add-business" onClick={() => setEditing({})} className="rounded-full h-11 bg-[#FF3B6B] hover:bg-[#E02E5A] text-white font-semibold">
          <Plus className="h-4 w-4 mr-1.5" /> Add business
        </Button>
      </div>

      <div data-testid="vendor-subscription-status" className={`mt-8 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4 border ${active ? "border-[#00E5A3]/40 bg-[#00E5A3]/5" : "border-[#FFB800]/40 bg-[#FFB800]/5"}`}>
        <div className="flex items-start gap-3">
          {active ? <CheckCircle2 className="h-5 w-5 text-[#00E5A3] mt-0.5" /> : <AlertTriangle className="h-5 w-5 text-[#FFB800] mt-0.5" />}
          <div>
            <p className="font-semibold">{active ? "Your listings are live" : "Your listings are not visible yet"}</p>
            <p className="text-sm text-slate-400">{active ? "Travelers searching your city see your business as Featured." : "Activate your $25/month vendor plan to go live in search results."}</p>
          </div>
        </div>
        {!active && <Button data-testid="vendor-activate-plan" onClick={subscribe} className="rounded-full bg-[#FFB800] hover:bg-[#E6A600] text-[#0B0F19] font-semibold">Activate — $25/mo</Button>}
      </div>

      <div className="mt-8 grid gap-4">
        {items.length === 0 && (
          <div data-testid="vendor-empty" className="rounded-2xl border border-dashed border-[#26334D] p-12 text-center text-slate-400">
            <Store className="h-8 w-8 mx-auto mb-3 text-slate-500" /> No businesses yet. Add your first one — it takes about a minute.
          </div>
        )}
        {items.map((b) => {
          const cat = CATEGORY_MAP[b.category];
          return (
            <div key={b.id} data-testid={`vendor-business-${b.id}`} className="rounded-2xl bg-[#151C2C] border border-[#26334D] p-5 flex flex-wrap items-center gap-4">
              <img src={imgSrc(b.image_url) || cat?.img} alt="" className="h-16 w-16 rounded-xl object-cover" />
              <div className="flex-1 min-w-[200px]">
                <h3 className="font-semibold text-lg">{b.name}</h3>
                <p className="text-sm text-slate-400">{cat?.label} · {b.city}</p>
                <p className="text-xs text-slate-500 mt-1">{b.schedule.length} show times · Updated {new Date(b.updated_at).toLocaleDateString()}</p>
              </div>
              <Button data-testid={`vendor-edit-${b.id}`} variant="outline" onClick={() => setEditing(b)} className="rounded-full border-[#26334D] bg-transparent"><Pencil className="h-4 w-4 mr-1.5" /> Update</Button>
              <Button data-testid={`vendor-delete-${b.id}`} variant="ghost" size="icon" onClick={() => remove(b.id)}><Trash2 className="h-4 w-4 text-[#FF7A7A]" /></Button>
            </div>
          );
        })}
      </div>

      {items.length > 0 && <VendorInsights />}
      {items.length > 0 && <VendorReviews />}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="business-form-modal" className="bg-[#151C2C] border-[#26334D] text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl font-bold">{editing?.id ? "Update business" : "Add your business"}</DialogTitle></DialogHeader>
          {editing && <BusinessForm initial={editing} onSubmit={save} busy={busy} />}
        </DialogContent>
      </Dialog>
    </main>
  );
}
